import { DatePipe } from '@angular/common';
import { Component, forwardRef, input, model, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { ICONS } from '@core/services/icons/icons.service';

import {
  FieldDate,
  FormBuilderBase
} from '@shared/components/row-edition-form/form-builder/form-builder.model';

import { unwrapValue, wrapValue } from '../row-edition-form.utils';

@Component({
  selector: 'app-date',
  imports: [DatePipe],
  templateUrl: './date.component.html',
  styles: [
    `
      :host {
        display: flex;
        width: 100%;
      }
    `
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateComponent),
      multi: true
    }
  ]
})
export class DateComponent implements ControlValueAccessor {
  protected readonly ICONS = ICONS;

  id = input<FormBuilderBase['id']>();
  value = model.required<Date | null>();
  min = input<FieldDate['minDate']>();
  max = input<FieldDate['maxDate']>();
  required = input<FormBuilderBase['required']>();
  needTime = input<FieldDate['needTime']>();
  isArray = input<FormBuilderBase['isArray']>(false);
  wrapperProperty = input<string | undefined>(undefined);

  /** Backs the array variant; the single-value variant uses `value` instead. */
  protected dates = signal<string[]>([]);

  onChange = (_value: string) => {};
  onTouched = () => {};

  onInput(event: Event) {
    const inputValue = (event.target as HTMLInputElement).value;

    // Browser gives us: "2023-12-12T07:46"
    // We need to convert to a proper Date object
    // Adding seconds ensures valid ISO 8601 format
    const isoString = this.needTime()
      ? `${inputValue}:00` // "2023-12-12T07:46:00"
      : inputValue; // "2023-12-12"

    const date = new Date(isoString);

    // Validate the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date input:', inputValue);
      return;
    }

    this.onChange(isoString);
  }

  addDate(event: Event): void {
    const input = event.target as HTMLInputElement;
    const picked = input.value;

    if (picked && !this.dates().includes(picked)) {
      // The picker yields `YYYY-MM-DD`, so a lexicographic sort is chronological.
      this.dates.set([...this.dates(), picked].sort());
      this.emitDates();
    }

    input.value = '';
    this.onTouched();
  }

  removeDate(date: string): void {
    this.dates.set(this.dates().filter((value) => value !== date));
    this.emitDates();
    this.onTouched();
  }

  private emitDates(): void {
    // Always an array, never null: these schemas type the field as `array` with a
    // `[]` default, so a cleared list must be sent as empty rather than nulled out.
    this.onChange(this.dates() as unknown as string);
  }

  writeValue(value: Date): void {
    const unwrapped = unwrapValue(value, this.wrapperProperty());

    if (this.isArray()) {
      this.dates.set(Array.isArray(unwrapped) ? (unwrapped as string[]) : []);
      return;
    }

    this.value.set(unwrapped as Date);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnChange(fn: any): void {
    this.onChange = (value: string) => fn(wrapValue(value, this.wrapperProperty()));
  }
  registerOnTouched(fn: never): void {
    this.onTouched = fn;
  }
}
